import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { generateKit } from '../../../actions/coachCertificationActions'
import { showGenericError } from '../../../utility/helperFunctions'
import { isKitLoading } from '../../../utility/coachCertification'
import Lightbox from 'yet-another-react-lightbox'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import Button from './Button'

import classes from './ShareKitCard.module.scss'

const ShareKitCard = ({ certification }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const kitStatus = useSelector(state => state.coachCertification.kitStatus)
  const { enrollment, level } = certification
  const kitLoading = isKitLoading(enrollment, kitStatus)
  const generate = () => dispatch(generateKit(enrollment.id)).unwrap().catch(showGenericError)
  const [copied, setCopied] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(null)
  const canCopy = !!navigator.clipboard
  const club = enrollment.clubName ? t('portal.certification.share.captionClub', { name: enrollment.clubName }) : ''
  const caption = t('portal.certification.share.caption', { level, club })
  const files = [
    {
      kind: 'certificate',
      title: t('portal.certification.share.certificate'),
      meta: t('portal.certification.share.certificateMeta'),
      url: enrollment.certificateUrl,
      previewUrl: enrollment.certificatePreviewUrl,
    },
    {
      kind: 'post',
      title: t('portal.certification.share.post'),
      meta: '1080 × 1080',
      url: enrollment.postImageUrl,
      previewUrl: enrollment.postImageUrl,
    },
    {
      kind: 'story',
      title: t('portal.certification.share.story'),
      meta: '1080 × 1920',
      url: enrollment.storyImageUrl,
      previewUrl: enrollment.storyImageUrl,
    },
  ]

  const copyCaption = () => navigator.clipboard.writeText(caption).then(() => setCopied(true)).catch(() => setCopied(false))

  useEffect(() => {
    if (!enrollment.kitReady && kitStatus === 'idle') { generate() }
  }, [enrollment.kitReady, kitStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!copied) { return undefined }

    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  return (
    <section id='share' className={classes.card}>
      <span className={classes.label}>{t('portal.certification.share.eyebrow')}</span>
      <h2 className={classes.title}>{t('portal.certification.share.title')}</h2>
      <p className={classes.text}>{t('portal.certification.share.text')}</p>

      <div className={classes.kit}>
        {files.map((file, index) => (
          <div key={file.kind}>
            {enrollment.kitReady
              ? (
                <button type='button' className={classes.tile} onClick={() => setPreviewIndex(index)} aria-label={file.title}>
                  <img src={file.previewUrl} alt='' className={classNames(classes.preview, classes[file.kind])} />
                </button>
              )
              : (
                <div
                  className={classNames('d-flex align-items-center justify-content-center', classes.preview, classes.placeholder, classes[file.kind])}
                >
                  {kitLoading && <i className='fa fa-spinner fa-spin' aria-hidden='true' />}
                </div>
              )}
            <div className={classNames('d-flex align-items-center justify-content-between', classes.fileRow)}>
              <span>
                <span className={classes.fileTitle}>{file.title}</span>
                <span className={classes.fileMeta}>{file.meta}</span>
              </span>
              {enrollment.kitReady && (
                <a
                  href={file.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={classes.fileButton}
                  aria-label={t('portal.certification.share.downloadFile', { name: file.title })}
                >
                  <i className='fa fa-download' aria-hidden='true' />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
      {!enrollment.kitReady && (
        <div className={classNames('d-flex align-items-center flex-wrap', classes.pending)}>
          <span>{t(`portal.certification.share.${kitLoading ? 'preparing' : 'missing'}`)}</span>
          {!kitLoading && (
            <Button variant='outline' onClick={generate}>{t('portal.certification.share.generate')}</Button>
          )}
        </div>
      )}

      <div className={classNames('d-flex align-items-baseline justify-content-between', classes.captionHead)}>
        <span className={classes.label}>{t('portal.certification.share.captionTitle')}</span>
        {canCopy && (
          <button type='button' className={classes.copy} onClick={copyCaption}>
            <i className='fa fa-clone mr-2' aria-hidden='true' />
            {t(copied ? 'portal.certification.share.copied' : 'portal.certification.share.copy')}
          </button>
        )}
      </div>
      <p className={classes.caption}>{caption}</p>

      <Lightbox
        open={previewIndex !== null}
        index={previewIndex ?? 0}
        close={() => setPreviewIndex(null)}
        slides={files.map(file => ({ src: file.previewUrl, description: file.title }))}
        plugins={[Captions]}
        carousel={{ finite: true }}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' } }}
      />
    </section>
  )
}

ShareKitCard.propTypes = {
  certification: PropTypes.shape({
    level: PropTypes.number.isRequired,
    enrollment: PropTypes.shape({
      id: PropTypes.number.isRequired,
      kitReady: PropTypes.bool,
      certificateUrl: PropTypes.string,
      certificatePreviewUrl: PropTypes.string,
      postImageUrl: PropTypes.string,
      storyImageUrl: PropTypes.string,
      clubName: PropTypes.string,
    }).isRequired,
  }).isRequired,
}

export default ShareKitCard
